/**
 * The proposals register.
 *
 * `expired` is a derived column, not a status: the API computes it from
 * `valid_until` on read rather than running a job whose only purpose is to make
 * a date comparison true. So a lapsed offer shows as "Sent · lapsed" rather
 * than as a fifth status that would have to be kept in step.
 *
 * Versions share a `reference`. A revision is a new document, and the register
 * shows both — the original is what the client received, and hiding it would
 * lose the trail.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CalendarDays, FileText, Plus } from 'lucide-react';
import {
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  formatMoney,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

import { NewProposalDialog } from './new-proposal-dialog';

export interface ProposalRow {
  public_id: string;
  code: string;
  title: string;
  status: string;
  client_public_id: string;
  client_name: string;
  account_manager_name: string | null;
  currency: string;
  subtotal: string;
  tax_amount: string;
  total: string;
  issued_on: string | null;
  valid_until: string | null;
  expired: boolean;
  version: number;
  line_count: number;
  created_at: string;
}

export const PROPOSAL_STATUS_TONES: Record<string, Tone> = {
  draft: 'neutral',
  sent: 'info',
  accepted: 'success',
  declined: 'danger',
  withdrawn: 'neutral',
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const ENDPOINT = '/crm/proposals';

export function ProposalsPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [recentKey, setRecentKey] = useState<string | null>(null);

  const fetchRows = useMemo(() => createServerFetcher<ProposalRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<ProposalRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Reference',
        filter: 'text',
        width: 130,
        render: (row) => (
          <span className="flex items-center gap-1">
            <span className="font-mono text-xs text-muted">{row.code}</span>
            {/* Only from version two: "v1" on a document nobody revised is
                noise on every row. */}
            {row.version > 1 ? (
              <span className="rounded bg-surface-3 px-1 text-2xs text-faint">
                v{row.version}
              </span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'title',
        header: 'Proposal',
        icon: <FileText className="size-3" />,
        filter: 'text',
        minWidth: 240,
        render: (row) => <span className="truncate font-medium">{row.title}</span>,
      },
      {
        field: 'client_name',
        header: 'Client',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 180,
      },
      {
        field: 'status',
        header: 'Status',
        filter: 'select',
        options: STATUS_OPTIONS,
        width: 150,
        render: (row) => (
          <span className="flex items-center gap-1.5">
            <StatusChip value={row.status} tones={PROPOSAL_STATUS_TONES} />
            {row.expired ? (
              <span className="text-2xs text-warning" title="Past its valid-until date">
                lapsed
              </span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'total',
        header: 'Total',
        filter: 'number',
        numeric: true,
        width: 140,
        format: (value, row) => formatMoney(value as string, row.currency),
      },
      {
        field: 'issued_on',
        header: 'Sent',
        icon: <CalendarDays className="size-3" />,
        filter: 'date',
        width: 125,
        format: (value) => formatDate(value as string | null),
      },
      {
        field: 'valid_until',
        header: 'Valid until',
        filter: 'date',
        width: 130,
        render: (row) => (
          <span className={row.expired ? 'text-warning' : undefined}>
            {formatDate(row.valid_until)}
          </span>
        ),
      },
      {
        field: 'account_manager_name',
        header: 'Owner',
        filter: 'select',
        width: 160,
        hiddenByDefault: true,
      },
      {
        field: 'line_count',
        header: 'Lines',
        numeric: true,
        width: 85,
        sortable: false,
        hiddenByDefault: true,
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Proposals"
        description="What has been offered, and what came of it."
        actions={
          <Can permission="crm.proposal.create">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" />
              New proposal
            </Button>
          </Can>
        }
      />

      <ServerTable<ProposalRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['crm', 'proposals']}
        stateKey="crm.proposals"
        searchPlaceholder="Search by title, reference or client"
        viewField="title"
        onView={(row) => navigate(`/proposals/${row.public_id}`)}
        recentKey={recentKey}
        rowDetailFields={(row) => [
          { label: 'Client', value: row.client_name },
          { label: 'Owner', value: row.account_manager_name },
          { label: 'Subtotal', value: formatMoney(row.subtotal, row.currency) },
          { label: 'Tax', value: formatMoney(row.tax_amount, row.currency) },
          { label: 'Total', value: formatMoney(row.total, row.currency) },
          { label: 'Sent', value: formatDate(row.issued_on) },
          { label: 'Valid until', value: formatDate(row.valid_until) },
          { label: 'Version', value: String(row.version) },
        ]}
        emptyTitle="No proposals yet"
        emptyDescription="A proposal is what you offer before there is a project. Accepting one can open the project it describes."
        emptyAction={
          <Can permission="crm.proposal.create">
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus className="size-3.5" />
              Draft a proposal
            </Button>
          </Can>
        }
      />

      <NewProposalDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(publicId) => {
          setCreating(false);
          setRecentKey(publicId);
          navigate(`/proposals/${publicId}`);
        }}
      />
    </div>
  );
}
